import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { dashboardRoutes } from "../routes/DashboardRoutes";
import { campaignHomeLink, subRouteLink } from "../utils/routeLinks";
import { useBreadcrumbTitle } from "../components/dashboard-v2/BreadcrumbTitle";

const HOME_PATH = "/dashboard/home";

// Breadcrumb items for the current page, read from dashboardRoutes (a route's optional
// `breadcrumbLabel` wins over its `label`):
// - a page inside a section: "<Section> / <Page>", the section being a link
// - a section's own home page: "<Section>"
// - a single page outside any section: "<Page>"
// The last item is the current page.
export function useBreadcrumbs() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const pageTitle = useBreadcrumbTitle();
  const crumbs = buildBreadcrumbs(pathname, search, navigate);
  if (!pageTitle || crumbs.length === 0) return crumbs;
  // A page can name itself: that replaces the last crumb (a campaign's name), or is added after
  // it (the profile's tab; the crumb before it then links back to the page's default).
  if (pageTitle.append) {
    const last = crumbs[crumbs.length - 1];
    return [
      ...crumbs.slice(0, -1),
      { ...last, onClick: () => navigate(pathname) },
      { label: pageTitle.label },
    ];
  }
  // Replaces the last crumb when there is a page crumb to replace; otherwise (the name wasn't in
  // the URL) it is added after the section.
  return crumbs.length > 1
    ? [...crumbs.slice(0, -1), { label: pageTitle.label }]
    : [...crumbs, { label: pageTitle.label }];
}

function buildBreadcrumbs(pathname, search, navigate) {
  if (pathname === HOME_PATH) return [{ label: "Home" }];

  const route = dashboardRoutes.find((r) => {
    const base = r.path.split("?")[0];
    return pathname === base || pathname.startsWith(`${base}/`);
  });
  // Onboarding steps have their own stepper, and unknown paths have nothing to show.
  if (!route || route.areSubRoutesGroupedSidebarTabs) return [];

  const routeLabel = route.breadcrumbLabel ?? route.label;

  if (!route.routes) return [{ label: routeLabel }];

  let sub = null;
  let params = {};
  for (const candidate of route.routes) {
    const match = matchPath(
      { path: `${route.path}/${candidate.path}`, end: true },
      pathname,
    );
    if (match) {
      sub = candidate;
      params = match.params;
      break;
    }
  }
  // breadcrumbLabel may be a function of the URL params and query; null means "no page crumb".
  const custom = typeof sub?.breadcrumbLabel === "function";
  const subLabel = !sub
    ? null
    : custom
      ? sub.breadcrumbLabel(params, new URLSearchParams(search))
      : (sub.breadcrumbLabel ?? sub.label);
  const goToSectionDefault = () =>
    navigate(
      route.path === "/campaign"
        ? campaignHomeLink()
        : subRouteLink(route.path, route.routes[0].path),
    );

  if (sub && !custom && sub.path === "home") {
    // A section with exactly one page (e.g. Help/FAQs): nothing else to link to.
    return [{ label: routeLabel }];
  }
  if (!subLabel) {
    // A dynamic breadcrumbLabel (e.g. the campaign's name from ?name=) hasn't resolved yet —
    // still a real sub-page, so the section crumb should stay clickable, not go dead.
    return [{ label: routeLabel, onClick: goToSectionDefault }];
  }
  if (subLabel === routeLabel) {
    return [{ label: routeLabel }];
  }

  return [
    { label: routeLabel, onClick: goToSectionDefault },
    { label: subLabel },
  ];
}
